export interface Store {
    id: string;
    name: string;
    baseUrl: string;
    enabled: boolean;
    trackingInterval: number;
    batchSize: number;
    lastSync?: string;
    createdAt: string;
    updatedAt: string;
}

export interface StoreState {
    storeId: string;
    storeConfig: {
        name: string;
        baseUrl: string;
        enabled: boolean;
    };
    syncStatus: string;
    lastFullSync?: string;
    lastPage1Sync?: string;
    highestOrderId?: number;
    totalPages?: number;
    needsFullSync: boolean;
    consecutiveErrors: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SmartSystemStatus {
    scheduler: {
        jobs: {
            woocommerce_sync: {
                isActive: boolean;
                instances: Record<string, any>;
            };
        };
        stats: {
            totalJobs: number;
            activeJobs: number;
            completedJobs: number;
            failedJobs: number;
            lastHealthCheck: string;
        };
        globalConfig: {
            maxConcurrentJobs: number;
            defaultRetryAttempts: number;
            retryDelay: number;
            healthCheckInterval: number;
        };
    };
    smartManager: {
        totalStores: number;
        activeStores: number;
        totalOrders: number;
        newOrders: number;
        updatedOrders: number;
        errors: number;
        lastSync: string;
        storeStates: Record<string, StoreState>;
        settings: {
            batchSize: number;
            maxConsecutiveErrors: number;
            retryDelay: number;
            fullSyncThreshold: number;
        };
    };
    isInitialized: boolean;
}

export interface WooCommerceOrder {
    _id: string;
    woocommerceId: number;
    orderNumber: string;
    status: string;
    currency: string;
    total: number;
    customerEmail: string;
    customerPhone: string;
    billingAddress: {
        country: string;
    };
    store?: {
        id: string;
        name: string;
        baseUrl: string;
        enabled: boolean;
    };
    createdAt: string;
    updatedAt: string;
}

export type TabType = "orders" | "stores";

export interface WooCommerceStats {
    totalStores: number;
    activeStores: number;
    totalOrders: number;
    ordersByDomain: Record<string, number>;
}

export interface OrderFilters {
    search: string;
    status: string;
    sortBy: 'orderNumber' | 'status' | 'customerEmail' | 'total' | 'createdAt';
    sortOrder: 'asc' | 'desc';
}

export interface PaginationState {
    currentPage: number;
    itemsPerPage: number;
}

// Helper functions
export const getStoreById = (stores: Store[], id: string): Store | undefined => {
    return stores.find(store => store.id === id);
};

export const getOrdersByDomain = (orders: WooCommerceOrder[]): Record<string, WooCommerceOrder[]> => {
    const grouped: Record<string, WooCommerceOrder[]> = {};
    orders.forEach(order => {
        const domain = order.store?.baseUrl || "Unknown Domain";
        if (!grouped[domain]) {
            grouped[domain] = [];
        }
        grouped[domain].push(order);
    });
    return grouped;
};

export const getOrderStats = (orders: WooCommerceOrder[]): WooCommerceStats => {
    const ordersByDomain = getOrdersByDomain(orders);
    const domainCounts = Object.keys(ordersByDomain).reduce((acc, domain) => {
        acc[domain] = ordersByDomain[domain].length;
        return acc;
    }, {} as Record<string, number>);

    return {
        totalStores: Object.keys(ordersByDomain).length,
        activeStores: Object.keys(ordersByDomain).length, // All stores with orders are considered active
        totalOrders: orders.length,
        ordersByDomain: domainCounts
    };
};

export const filterAndSortOrders = (
    orders: WooCommerceOrder[],
    filters: OrderFilters
): WooCommerceOrder[] => {
    let filtered = orders.filter(order => {
        const matchesSearch = !filters.search ||
            order.orderNumber.toLowerCase().includes(filters.search.toLowerCase()) ||
            order.customerEmail.toLowerCase().includes(filters.search.toLowerCase()) ||
            order.customerPhone?.toLowerCase().includes(filters.search.toLowerCase());

        const matchesStatus = !filters.status || order.status === filters.status;

        return matchesSearch && matchesStatus;
    });

    // Sort orders
    filtered.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (filters.sortBy) {
            case 'orderNumber':
                aValue = a.orderNumber;
                bValue = b.orderNumber;
                break;
            case 'status':
                aValue = a.status;
                bValue = b.status;
                break;
            case 'customerEmail':
                aValue = a.customerEmail;
                bValue = b.customerEmail;
                break;
            case 'total':
                aValue = a.total;
                bValue = b.total;
                break;
            case 'createdAt':
                aValue = new Date(a.createdAt).getTime();
                bValue = new Date(b.createdAt).getTime();
                break;
            default:
                return 0;
        }

        if (filters.sortOrder === 'asc') {
            return aValue > bValue ? 1 : -1;
        } else {
            return aValue < bValue ? 1 : -1;
        }
    });

    return filtered;
};
